/**
 * Slider for a keyframable Param with the standard NLE affordance cluster:
 * stopwatch (toggle animated), ‹ ◆ › (prev keyframe / add-remove keyframe at
 * playhead / next keyframe). While animated, the shown value is sampled at the
 * playhead (throttled to 10Hz) and edits write a keyframe at the playhead.
 */
import { clipParamTime, isAnimated, paramKeyframes, prevNextKeyframeTimes, sampleParam, KEYFRAME_EPS } from '../../format/params';
import type { AnyClip, Param } from '../../format/types';
import { usePlayback } from '../../state/playback';
import { useTimeline } from '../../state/timeline';
import { Slider } from './fields';

/** Playhead time quantized to 0.1s — keeps inspector re-renders at ≤10Hz during playback. */
export function usePlayheadCoarse(): number {
  return usePlayback((s) => Math.round(s.currentTime * 10) / 10);
}

/** Timeline time of a keyframe (axis time → timeline seconds). */
export function keyframeTimelineTime(clip: AnyClip, axisT: number): number {
  if ('in' in clip) return clip.start + (axisT - clip.in) / (clip.speed ?? 1);
  return clip.start + axisT;
}

const KF_EPS = 1 / 30; // half a frame-ish: "a keyframe exists at the playhead"

export default function ParamSlider({
  clip,
  path,
  param,
  label,
  min,
  max,
  step,
  onStatic,
  onSeek,
}: {
  clip: AnyClip;
  path: string;
  param: Param;
  label: string;
  min: number;
  max: number;
  step: number;
  /** plain patch used while the param is static (existing behavior) */
  onStatic: (v: number) => void;
  /** seek the playhead (prev/next keyframe navigation); optional */
  onSeek?: (t: number) => void;
}) {
  const coarseT = usePlayheadCoarse();
  const animated = isAnimated(param);
  const axisT = clipParamTime(clip, animated ? usePlayback.getState().currentTime : coarseT);
  const value = sampleParam(param, axisT);
  const store = () => useTimeline.getState();
  const playhead = () => usePlayback.getState().currentTime;

  const kfs = paramKeyframes(param);
  const hasKeyframeHere = animated && kfs.some((k) => Math.abs(k.t - axisT) <= KF_EPS);
  const { prev, next } = animated
    ? prevNextKeyframeTimes([param], axisT, KEYFRAME_EPS)
    : { prev: null, next: null };

  return (
    <div className={`insp-param ${animated ? 'animated' : ''}`}>
      <Slider
        label={label}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(v) => {
          if (animated) store().setParamKeyframe(clip.id, path, v, playhead());
          else onStatic(v);
        }}
      />
      <span className="insp-kf-cluster">
        {animated && (
          <>
            <button
              className="ghost icon insp-kf-nav"
              title="Previous keyframe"
              disabled={prev === null || !onSeek}
              onClick={() => prev !== null && onSeek?.(keyframeTimelineTime(clip, prev))}
            >
              ‹
            </button>
            <button
              className={`ghost icon insp-kf-diamond ${hasKeyframeHere ? 'active' : ''}`}
              title={hasKeyframeHere ? 'Remove keyframe at playhead' : 'Add keyframe at playhead'}
              onClick={() => {
                if (hasKeyframeHere) store().removeParamKeyframe(clip.id, path, playhead());
                else store().setParamKeyframe(clip.id, path, value, playhead());
              }}
            >
              ◆
            </button>
            <button
              className="ghost icon insp-kf-nav"
              title="Next keyframe"
              disabled={next === null || !onSeek}
              onClick={() => next !== null && onSeek?.(keyframeTimelineTime(clip, next))}
            >
              ›
            </button>
          </>
        )}
        <button
          className={`ghost icon insp-kf-toggle ${animated ? 'active' : ''}`}
          title={animated ? 'Remove animation (bake current value)' : 'Animate this parameter'}
          onClick={() => store().toggleParamAnimated(clip.id, path, playhead())}
        >
          ⏱
        </button>
      </span>
    </div>
  );
}
